import { Injectable, OnDestroy, inject } from '@angular/core';
import { UserIdleLogoutModalComponent } from '@components/common/user-idle-logout-modal/user-idle-logout-modal.component';
import { ModalController, Platform } from '@ionic/angular';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { AuthStateService } from '@services/auth/auth-state.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { BehaviorSubject, Subscription, distinctUntilChanged, filter, tap } from 'rxjs';
import { FrontendConfiguration } from 'src/configuration/config.patientportal';
@Injectable({
    providedIn: 'root',
})
export class UserIdleService implements OnDestroy {
    private idle = inject(Idle);
    private keepalive = inject(Keepalive);
    private authService = inject(AuthStateService);
    private navService = inject(NavigationService);
    private modalCtrl = inject(ModalController);
    private subscriptions: Subscription[] = [];
    private modal: HTMLIonModalElement | null = null;
    private secondsTillTimeoutSubject: BehaviorSubject<number> | null = null;
    private timedOut = false;
    private platform = inject(Platform);
    constructor() {
        const config = new FrontendConfiguration();
        this.idle.setIdle(config.userIdleSeconds); // seconds
        this.idle.setTimeout(config.userTimeoutSeconds); // seconds
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        // sets the ping interval to 15 seconds
        this.keepalive.interval(15);
        this.keepalive.start();

        this.subscriptions.push(this.idle.onIdleStart.subscribe(async () => await this.handleIdleStart()));
        this.subscriptions.push(this.idle.onTimeoutWarning.subscribe((countdown) => this.handleTimeoutWarning(countdown)));
        this.subscriptions.push(this.idle.onTimeout.subscribe(() => this.handleTimeout()));
        this.subscriptions.push(
            this.authService
                .getIsLogged() // will emit also after silent refresh, even though loggedIn doesn't change
                .pipe(distinctUntilChanged()) // emit only when loggedIn changed - ignore silent refresh
                .subscribe((isLoggedIn) => this.handleIsLoggedIn(isLoggedIn))
        );

        this.platform.pause
            .pipe(
                filter(() => this.authService.isLogged() === true),
                tap(() => {
                    // don't stop the timer if you pause the app when idling, the idle timer will resume when you resume the app
                    if (!this.idle.isIdling()) this.idle.stop();
                })
            )
            .subscribe();

        this.platform.resume
            .pipe(
                tap(() => {
                    if (this.authService.isLogged() === true) {
                        // start measuring idle time if you weren't idling before
                        if (!this.idle.isIdling()) this.idle.watch();
                    } else {
                        // if you pause the app when having idle modal open and resume after it's timeouted, close the modal
                        this.modal?.dismiss();
                        this.reset();
                    }
                })
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.subscriptions = [];
    }

    private handleIsLoggedIn(isLoggedIn: boolean) {
        if (isLoggedIn) {
            this.idle.watch();
        } else {
            this.idle.stop();
        }
    }

    private async handleIdleStart() {
        if (this.modal !== null) return;

        this.timedOut = false;
        this.idle.clearInterrupts();
        this.secondsTillTimeoutSubject = new BehaviorSubject(this.idle.getTimeout());
        this.modal = await this.modalCtrl.create({
            component: UserIdleLogoutModalComponent,
            componentProps: {
                secondsTillTimeout$: this.secondsTillTimeoutSubject.asObservable(),
            },
            cssClass: 'small-modal',
            backdropDismiss: false,
        });

        // there is a weird issue related to thread syncing when you change tabs
        // in the browser. Apparently, when you switch to another tab and let the idle timer
        // and the timeout timer pass, the code will resume running from this point when you switch back
        // to this tab. This is because browsers deprioritize/slow down clocks on promises
        // that run on inactive tabs and once you switch back to a tab, its promises get their
        // threads synced at that moment. This will cause this.timedOut to be actually true
        // because handleTimeout() function has already run. In that case, don't show the modal,
        // the user has already been logged out
        if (!this.timedOut) {
            await this.modal.present();
            this.modal?.onDidDismiss().then((result) => {
                if (!result?.data) return;
                else {
                    this.reset();

                    if (result.data['stay']) {
                        this.stay();
                    } else if (result.data['logout']) {
                        this.logout();
                    }
                }
            });
        } else this.reset(); // reset for the next run
    }
    private handleTimeoutWarning(countdown: number) {
        if (this.modal) {
            this.secondsTillTimeoutSubject?.next(countdown);
        }
    }
    private handleTimeout() {
        this.timedOut = true;
        this.modal?.dismiss();
        this.reset();
        this.logout();
    }
    private stay() {
        this.idle.watch();
    }
    private logout() {
        this.authService.logout().subscribe();
    }
    private reset() {
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        this.modal = null;
        this.secondsTillTimeoutSubject = null;
    }
}
